<template>
  <BModal v-model="showModalInternal" :key="currentRoles" title="Role użytkownika" hide-footer scrollable>
    <div class="card shadow-sm mb-3">
      <div class="card-header bg-light">
        <h6 class="mb-0 text-primary fw-bold">
          <i class="fas fa-user-shield me-2"></i>Bieżące role użytkownika
        </h6>
      </div>
      <div class="card-body">
        <div v-if="currentRoles.length === 0" class="text-muted fst-italic">Brak przypisanych ról</div>
        <div v-else class="d-flex flex-wrap gap-2">
          <span
            v-for="role in currentRoles"
            :key="resolveRoleId(role)"
            class="badge bg-secondary fs-6"
          >
            {{ resolveRoleName(role) }}
          </span>
        </div>
      </div>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-header bg-light">
        <h6 class="mb-0 text-secondary fw-bold">
          <i class="fas fa-cogs me-2"></i>Aktualizacja ról
        </h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="form-label">Tryb aktualizacji</label>
            <BFormRadioGroup
              v-model="updateMode"
              :options="updateModeOptions"
              switches
            />
          </div>
          <div class="col-12">
            <label class="form-label">Dostępne role</label>
            <BFormSelect
              v-model="selectedRoleIds"
              :options="rolesOptions"
              multiple
              size="8"
            />
            <small class="text-muted mt-1 d-block">
              Przytrzymaj Ctrl / Cmd, aby zaznaczyć wiele ról.
            </small>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-secondary" @click="showModalInternal = false">Anuluj</button>
      <button type="button" class="btn btn-primary" @click="saveRoles" :disabled="!canSave">
        <i class="fas fa-save me-1"></i> Zapisz
      </button>
    </div>
  </BModal>
</template>

<script>
import { BModal, BFormSelect, BFormRadioGroup } from "bootstrap-vue-next";
import { useUserRbacManagerModule } from "@/state/pinia/userRbacManagerModule";
import { normalizeRoleName } from "@/helpers/rbacAccess";
import Swal from "sweetalert2";

export default {
  name: "UserRolesModal",
  components: { BModal, BFormSelect, BFormRadioGroup },
  props: {
    showModal: { type: Boolean, required: true },
    user: { type: Object, required: true },
    rolesCatalog: { type: Array, default: () => [] },
  },
  emits: ["update:showModal", "rolesUpdated"],
  data() {
    return {
      showModalInternal: this.showModal,
      currentRoles: [],
      selectedRoleIds: [],
      updateMode: "replace",
      updateModeOptions: [
        { value: "replace", text: "Zastąp" },
        { value: "add", text: "Dodaj" },
        { value: "remove", text: "Usuń" },
      ],
    };
  },
  computed: {
    rbacModule() {
      return useUserRbacManagerModule();
    },
    rolesOptions() {
      return this.rolesCatalog
        .map((role) => ({
          value: this.resolveRoleId(role),
          text: this.resolveRoleName(role),
        }))
        .filter((option) => option.value);
    },
    canSave() {
      return this.updateMode === "replace" || this.selectedRoleIds.length > 0;
    },
  },
  watch: {
    showModal(newValue) {
      this.loadUserRoles().then(()=>{
        this.showModalInternal = newValue;
      })


    },
    showModalInternal(newValue) {
      this.$emit("update:showModal", newValue);
    },
  },
  methods: {
    resolveRoleId(role) {
      return role?.id || role;
    },
    resolveRoleName(role) {
      return normalizeRoleName(role);
    },
    resolveUserEmail(user) {
      console.log("User:",user);
      return user.email;
    },
    loadUserRoles() {
      const userEmail = this.resolveUserEmail(this.user);
      console.log("User email:",userEmail);
      if (!userEmail) return;
      return this.rbacModule.httpGetUserRolesByEmail({ email: userEmail }).then((response) => {
        console.log("Users roles:",response);
        const payload = response?.data?.payload || response?.data || response?.payload || [];
        const rolesList = Array.isArray(payload) ? payload : payload.content || [];
        this.currentRoles = rolesList;
        this.selectedRoleIds = rolesList.map((role) => this.resolveRoleId(role)).filter(Boolean);
        return new Promise((resolve) => resolve());
      });
    },
    resolveRolesByIds(roleIds) {
      return roleIds
        .map((roleId) => this.rolesCatalog.find((role) => this.resolveRoleId(role) === roleId))
        .filter(Boolean);
    },
    saveRoles() {
      const userEmail = this.resolveUserEmail(this.user);
      if (!userEmail) return;

      if (this.updateMode === "replace" && this.selectedRoleIds.length === 0) {
        Swal.fire({
          title: "Usunąć wszystkie role?",
          text: "Ta operacja usunie wszystkie role użytkownika.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#dc3545",
          confirmButtonText: "Usuń role",
          cancelButtonText: "Anuluj",
        }).then((result) => {
          if (result.isConfirmed) {
            this.performRoleUpdate(userEmail);
          }
        });
        return;
      }

      this.performRoleUpdate(userEmail);
    },
    performRoleUpdate(userEmail) {
      let requestPromise = null;
      if (this.updateMode === "add") {
        requestPromise = this.rbacModule.httpUpdateUserRolesAddByEmail({
          email: userEmail,
          roleIds: this.selectedRoleIds,
        });
      } else if (this.updateMode === "remove") {
        requestPromise = this.rbacModule.httpUpdateUserRolesRemoveByEmail({
          email: userEmail,
          roleIds: this.selectedRoleIds,
        });
      } else {
        requestPromise = this.rbacModule.httpUpdateUserRolesReplaceByEmail({
          email: userEmail,
          roleIds: this.selectedRoleIds,
        });
      }

      if (!requestPromise) {
        return;
      }
      requestPromise.then(() => {
        let updatedRoles = [];
        if (this.updateMode === "add") {
          const currentRoleIds = this.currentRoles.map((role) => this.resolveRoleId(role));
          const combinedIds = Array.from(new Set([...currentRoleIds, ...this.selectedRoleIds]));
          updatedRoles = this.resolveRolesByIds(combinedIds).map((role) => this.resolveRoleName(role));
        } else if (this.updateMode === "remove") {
          const removedIds = new Set(this.selectedRoleIds);
          const remainingIds = this.currentRoles
            .map((role) => this.resolveRoleId(role))
            .filter((roleId) => !removedIds.has(roleId));
          updatedRoles = this.resolveRolesByIds(remainingIds).map((role) => this.resolveRoleName(role));
        } else {
          updatedRoles = this.resolveRolesByIds(this.selectedRoleIds).map((role) => this.resolveRoleName(role));
        }

        this.$emit("rolesUpdated", {
          email: userEmail,
          roles: updatedRoles,
        });
        this.showModalInternal = false;
      });
    },
  },
  created() {
    this.loadUserRoles();
  }
};
</script>
